import { useEffect, useMemo, useRef, useState } from "react";
import { api, downloadBlobResponse } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { isAdminInstalasi, isAdminProdi, isAdminKoas, istilahProdi } from "@/lib/roles";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Building2, Users, TrendingUp, FileSpreadsheet, FileText,
    ChevronDown, ChevronRight,
} from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { chartTooltipProps, formatAngkaTooltip } from "@/lib/chartTooltip";
import { toast } from "sonner";
import StickyHeader from "@/components/StickyHeader";
import RotasiKlinikViewTabs from "@/components/RotasiKlinikViewTabs";
import RincianInstalasi from "@/components/RincianInstalasi";

const BULAN_NAMA = [
    "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const now = new Date();
const THIS_YEAR = now.getFullYear();
const YEAR_OPTIONS = [THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1];

const JENIS_OPTIONS = [
    { v: "keduanya", label: "Stase & Jaga" },
    { v: "stase", label: "Stase saja" },
    { v: "jaga", label: "Jaga saja" },
];

// Warna seri grouped bar chart. TETAP per seri (Stase selalu teal, Jaga selalu
// kuning-hijau) apa pun filter/urutannya -- warna mengikuti entitas, bukan peringkat,
// supaya pembaca yg sudah hafal "Jaga itu yg hijau" tidak tertipu saat urutan berubah.
//
// Kedua hex ini BUKAN warna brand mentah: #267B84 gagal chroma floor (C=0.08, terbaca
// keabuan) dan #CDDC29 gagal lightness band (L=0.855) serta kontras 1.51:1 di latar
// terang. Ini step terdekat dari keluarga hue yg sama yg lolos seluruh cek palet
// (lightness band, chroma floor, pemisahan CVD, kontras) di mode terang & gelap.
const WARNA_STASE = "#0E9AA7";
const WARNA_JAGA = "#8A9A1E";
// Instalasi bisa puluhan; membuat chart setinggi itu tidak terbaca. Chart menampilkan
// yg terpadat saja, tabel di bawahnya tetap memuat SEMUA baris (itu "table view"-nya).
const TOP_N = 12;

// Nama instalasi RSCM panjang (sampai 65 karakter) dan mayoritas berawalan
// "Instalasi Pelayanan ..." -- pada data uji 11 dari 14. Memotong mentah di ekor
// membuat label sumbu jadi kembar & tidak bisa dibedakan ("Instalasi Pelayanan
// Kardios..." vs "Instalasi Pelayanan HIV da..."), krn potongannya jatuh tepat di
// bagian yg identik. Jadi awalan yg tidak membedakan dibuang dulu, baru dipotong
// kalau masih panjang. Nama UTUH tetap muncul di tooltip (Recharts memakai nilai
// kategori asli utk label tooltip, tickFormatter hanya mempengaruhi tampilan sumbu).
function labelSumbu(nama) {
    const inti = String(nama).replace(/^Instalasi\s+(Pelayanan\s+)?/i, "");
    return inti.length > 30 ? `${inti.slice(0, 29)}…` : inti;
}

// Tab "Per Instalasi" dari menu Rotasi Klinik: membalik sudut pandang halaman
// "Per Peserta" (RotasiKlinik.jsx) -- bukan "siapa yg belum mengisi", tapi "berapa
// PPDS yg ada di tiap instalasi bulan ini", lintas SEMUA prodi (backend sengaja
// tidak memakai row-level security di sini, lihat _compute_rotasi_per_instalasi).
// Halaman ini utk Admin UFP / Super Admin (semua instalasi), Admin Instalasi
// (instalasinya saja -- dipotong di server, require_monitoring_per_instalasi), dan Admin Prodi
// (PPDS prodinya saja), dan Admin Koas (peserta semua prodi Koas) -- keduanya hanya-baca, dipotong
// di server (_cakupan_prodi_per_instalasi). Utk
// Admin Instalasi: tanpa kolom NPM (server juga tidak mengirimnya) + filter
// "Sedang berlangsung hari ini" (bawaan mati, dihitung server: hanya_berlangsung).
export default function RotasiKlinikPerInstalasi() {
    const { user } = useAuth();
    const isInstalasi = isAdminInstalasi(user?.role);
    const isProdi = isAdminProdi(user?.role);
    const isKoas = isAdminKoas(user?.role);
    const istilah = istilahProdi(user?.role);
    const { effectiveTheme } = useTheme();
    const isDark = effectiveTheme === "dark";
    // Recharts render SVG mentah (bukan kelas Tailwind) -> tidak kena override CSS
    // global di index.css, jadi warna grid/axis di-set manual, sama spt Dashboard (tooltip: lib/chartTooltip.js).
    const chartGridStroke = isDark ? "#3A3A3A" : "#E2E8F0";
    const chartTickFill = isDark ? "#94A3B8" : "#475569";
    const chartCursorFill = isDark ? "rgba(148,163,184,0.12)" : "rgba(15,23,42,0.05)";

    const [bulan, setBulan] = useState(String(now.getMonth() + 1));
    const [tahun, setTahun] = useState(String(THIS_YEAR));
    const [jenis, setJenis] = useState("keduanya");
    const [berlangsung, setBerlangsung] = useState(false);
    // Parameter dikirim hanya bila filter dipakai -> permintaan peran lain tidak berubah.
    const params = { bulan, tahun, jenis, ...(isInstalasi && berlangsung ? { hanya_berlangsung: true } : {}) };
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState("");
    const [expanded, setExpanded] = useState(null); // instalasi_id yg rinciannya dibuka
    // Sama seperti halaman Per Peserta: request "Stase & Jaga" lintas semua prodi bisa
    // lambat, jadi respons lama yg datang belakangan tidak boleh menimpa state terbaru.
    const requestIdRef = useRef(0);

    const load = async () => {
        const reqId = ++requestIdRef.current;
        setLoading(true);
        try {
            const { data } = await api.get("/rotasi-klinik/per-instalasi", { params });
            if (reqId !== requestIdRef.current) return;
            setData(data);
            setExpanded(null); // instalasi yg terbuka belum tentu ada di periode/jenis baru
        } catch {
            if (reqId !== requestIdRef.current) return;
            toast.error("Gagal memuat sebaran PPDS per instalasi");
            setData(null);
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [bulan, tahun, jenis, berlangsung]);

    const download = async (format) => {
        setDownloading(format);
        try {
            const res = await api.get(`/rotasi-klinik/per-instalasi/export/${format}`, {
                params, responseType: "blob",
            });
            const fallback = `aura_rotasi-klinik_${tahun}_${String(bulan).padStart(2, "0")}`
                + `${jenis === "keduanya" ? "" : `_${jenis}`}.${format === "excel" ? "xlsx" : "pdf"}`;
            downloadBlobResponse(res, fallback);
        } catch {
            toast.error(`Gagal export ${format === "excel" ? "Excel" : "PDF"}`);
        } finally {
            setDownloading("");
        }
    };

    // Kolom Stase/Jaga hanya relevan kalau jenisnya ikut dihitung backend --
    // menampilkan kolom yg pasti 0 untuk semua baris cuma jadi noise.
    const showStase = jenis === "stase" || jenis === "keduanya";
    const showJaga = jenis === "jaga" || jenis === "keduanya";
    const colSpan = 2 + (showStase ? 1 : 0) + (showJaga ? 1 : 0);

    // rows sudah diurutkan backend (total desc), jadi slice = yg terpadat.
    const chartData = useMemo(
        () => (data?.rows || []).slice(0, TOP_N).map((r) => ({
            nama: r.instalasi_nama, stase: r.jumlah_stase, jaga: r.jumlah_jaga,
        })),
        [data],
    );
    // Tinggi dihitung dari jumlah baris + pita sumbu X (64px), supaya label sumbu
    // tidak terpotong & card tidak memunculkan scroll vertikal kecil di dalamnya.
    const chartHeight = Math.max(200, chartData.length * (showStase && showJaga ? 44 : 30) + 64);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Monitoring</div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                            Rotasi Klinik
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {isInstalasi
                                ? "Sebaran PPDS di instalasi Anda pada bulan terpilih. Klik instalasi untuk melihat rinciannya."
                                : isProdi
                                    ? "Sebaran PPDS prodi Anda per instalasi pada bulan terpilih. Klik instalasi untuk melihat rinciannya."
                                    : isKoas && user?.prodi_id
                                    ? "Sebaran peserta didik modul Anda per instalasi pada bulan terpilih. Klik instalasi untuk melihat rinciannya."
                                    : isKoas
                                    ? "Sebaran peserta didik Koas (semua modul Koas) per instalasi pada bulan terpilih. Klik instalasi untuk melihat rinciannya."
                                    : "Sebaran PPDS per instalasi pada bulan terpilih — seluruh prodi. Klik instalasi untuk melihat rinciannya."}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Select value={jenis} onValueChange={setJenis}>
                            <SelectTrigger className="w-40 h-10" data-testid="rk-inst-jenis-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {JENIS_OPTIONS.map((j) => <SelectItem key={j.v} value={j.v}>{j.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={bulan} onValueChange={setBulan}>
                            <SelectTrigger className="w-36 h-10" data-testid="rk-inst-bulan-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {BULAN_NAMA.slice(1).map((b, i) => <SelectItem key={i + 1} value={String(i + 1)}>{b}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={tahun} onValueChange={setTahun}>
                            <SelectTrigger className="w-24 h-10" data-testid="rk-inst-tahun-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {isInstalasi && (
                            <label className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-slate-200 text-sm text-slate-700 cursor-pointer">
                                <Switch checked={berlangsung} onCheckedChange={setBerlangsung} data-testid="rk-inst-berlangsung-switch" />
                                Sedang berlangsung hari ini
                            </label>
                        )}
                        <Button variant="outline" className="h-10" data-testid="rk-inst-export-excel"
                            onClick={() => download("excel")} disabled={!!downloading || !data}>
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            {downloading === "excel" ? "Menyiapkan..." : "Excel"}
                        </Button>
                        <Button variant="outline" className="h-10" data-testid="rk-inst-export-pdf"
                            onClick={() => download("pdf")} disabled={!!downloading || !data}>
                            <FileText className="w-4 h-4 mr-2" />
                            {downloading === "pdf" ? "Menyiapkan..." : "PDF"}
                        </Button>
                    </div>
                </div>
                <div className="mt-3">
                    <RotasiKlinikViewTabs active="instalasi" />
                </div>
            </StickyHeader>

            {loading ? (
                <div className="p-10 text-slate-500">Memuat data...</div>
            ) : !data ? (
                <div className="p-10 text-slate-500">Tidak ada data.</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#267B84] text-white flex items-center justify-center shrink-0">
                                    <Building2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Instalasi Aktif</div>
                                    <div className="text-2xl font-extrabold text-slate-900">{data.instalasi_aktif}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#60C0D0] text-[#0F2E30] flex items-center justify-center shrink-0">
                                    <Users className="w-5 h-5" />
                                </div>
                                <div>
                                    {/* Peserta unik lintas instalasi -- sengaja tidak sama dgn jumlah
                                        kolom Total ditotalkan (1 org bisa stase & jaga sekaligus). */}
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total PPDS Tercatat</div>
                                    <div className="text-2xl font-extrabold text-slate-900">{data.total_ppds}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#CDDC29] text-[#0F2E30] flex items-center justify-center shrink-0">
                                    <TrendingUp className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Instalasi Terpadat</div>
                                    {data.instalasi_terpadat ? (
                                        <>
                                            <div className="text-base font-extrabold text-slate-900 truncate" title={data.instalasi_terpadat.instalasi_nama}>
                                                {data.instalasi_terpadat.instalasi_nama}
                                            </div>
                                            <div className="text-xs text-slate-500">{data.instalasi_terpadat.total} penempatan</div>
                                        </>
                                    ) : (
                                        <div className="text-base font-extrabold text-slate-400">—</div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Chart duduk di antara kartu ringkasan & tabel. Tabel di bawahnya
                        sekaligus jadi "table view" chart ini: tiap angka tetap terbaca
                        tanpa hover, jadi tooltip memperkaya dan tidak mengunci data. */}
                    <Card className="border-slate-200 shadow-sm">
                        <CardContent className="p-6">
                            <h3 className="font-bold text-slate-800">
                                PPDS per Instalasi — {BULAN_NAMA[Number(bulan)]} {tahun}
                            </h3>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {data.rows.length > TOP_N
                                    ? `${TOP_N} instalasi terpadat dari ${data.rows.length} instalasi.`
                                    : "Semua instalasi dengan rotasi tercatat."}
                                {!showJaga && " Hanya segmen Stase."}
                                {!showStase && " Hanya segmen Jaga."}
                                {isInstalasi && berlangsung && " Hanya segmen yang sedang berlangsung hari ini."}
                                {" Awalan “Instalasi Pelayanan” disingkat di sumbu; nama utuh ada di tabel & tooltip."}
                            </p>
                            <div className="mt-4" style={{ height: chartHeight }}>
                                {chartData.length === 0 ? (
                                    <div className="h-full flex items-center justify-center text-sm text-slate-400">
                                        Belum ada data untuk periode ini.
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        {/* barGap 2px = celah warna-surface pemisah batang bertetangga
                                            (bukan stroke); barSize 10px menjaga mark tetap tipis. */}
                                        <BarChart
                                            data={chartData}
                                            layout="vertical"
                                            barGap={2}
                                            margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
                                        >
                                            {/* Grid hairline SOLID (bukan dashed): dash menambah derau
                                                visual & terbaca sbg "ambang batas" padahal cuma grid. */}
                                            <CartesianGrid stroke={chartGridStroke} horizontal={false} />
                                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: chartTickFill }} />
                                            {/* Tick kustom berupa SATU elemen <text>. YAxis bawaan Recharts
                                                membungkus label per kata begitu lebarnya mepet, dan label
                                                2 baris membuat band 44px berdesakan (terbukti saat dipotret).
                                                Satu <text> tidak pernah wrap; pemendekan diurus labelSumbu. */}
                                            <YAxis
                                                type="category"
                                                dataKey="nama"
                                                width={210}
                                                interval={0}
                                                tickLine={false}
                                                tick={({ x, y, payload }) => (
                                                    <text x={x} y={y} dy={4} textAnchor="end" fontSize={11} fill={chartTickFill}>
                                                        {labelSumbu(payload.value)}
                                                    </text>
                                                )}
                                            />
                                            <Tooltip {...chartTooltipProps} formatter={formatAngkaTooltip} cursor={{ fill: chartCursorFill }} />
                                            {/* Legend wajib begitu ada >=2 seri; utk 1 seri judul di atas
                                                sudah menyebutkan apa yg diplot, jadi kotak legend mubazir. */}
                                            {showStase && showJaga && (
                                                <Legend wrapperStyle={{ color: chartTickFill, fontSize: 12 }} />
                                            )}
                                            {showStase && (
                                                <Bar dataKey="stase" name="Stase" fill={WARNA_STASE} barSize={10} radius={[0, 4, 4, 0]} />
                                            )}
                                            {showJaga && (
                                                <Bar dataKey="jaga" name="Jaga" fill={WARNA_JAGA} barSize={10} radius={[0, 4, 4, 0]} />
                                            )}
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-4 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-600">
                            Sebaran per Instalasi — {BULAN_NAMA[Number(bulan)]} {tahun}
                            {" · "}{JENIS_OPTIONS.find((j) => j.v === jenis)?.label}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full compact">
                                <thead className="bg-slate-100">
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                        <th>Instalasi</th>
                                        {showStase && <th className="text-right">PPDS Stase</th>}
                                        {showJaga && <th className="text-right">PPDS Jaga</th>}
                                        <th className="text-right pr-4">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={colSpan} className="text-center py-10 text-slate-400">
                                                Belum ada rotasi klinik tercatat untuk periode ini.
                                            </td>
                                        </tr>
                                    ) : data.rows.map((r) => {
                                        const open = expanded === r.instalasi_id;
                                        return [
                                            <tr
                                                key={r.instalasi_id}
                                                className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer"
                                                onClick={() => setExpanded(open ? null : r.instalasi_id)}
                                                data-testid={`rk-inst-row-${r.instalasi_id}`}
                                            >
                                                <td className="font-semibold text-slate-800">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                                        {r.instalasi_nama}
                                                    </span>
                                                </td>
                                                {showStase && <td className="text-right text-slate-600">{r.jumlah_stase}</td>}
                                                {showJaga && <td className="text-right text-slate-600">{r.jumlah_jaga}</td>}
                                                <td className="text-right pr-4 font-bold text-slate-900">{r.total}</td>
                                            </tr>,
                                            open && (
                                                <tr key={`${r.instalasi_id}-detail`} className="border-t border-slate-200 bg-slate-50">
                                                    <td colSpan={colSpan} className="p-0">
                                                        <RincianInstalasi
                                                            key={r.instalasi_id} row={r} isInstalasi={isInstalasi}
                                                            showStase={showStase} showJaga={showJaga} istilah={istilah}
                                                        />
                                                    </td>
                                                </tr>
                                            ),
                                        ];
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}
